import * as RadixPopover from '@radix-ui/react-popover';
import classNames from 'classnames';
import type { ComponentPropsWithoutRef, FC, ReactNode, RefObject } from 'react';
import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useWindowDocument } from '../../../context/WindowDocumentContext';
import { Tooltip } from '../Tooltip/Tooltip';
import styles from './Popover.module.scss';

type Measurable = { getBoundingClientRect(): DOMRect };

interface IPopoverProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  modal?: boolean;
  /** Closes when the window loses focus, as menus do: also when the user clicks into an iframe. */
  closeOnWindowBlur?: boolean;
}

interface IPopoverTriggerProps {
  children: ReactNode;
  asChild?: boolean;
  tooltip?: string;
}

interface IPopoverContentProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  alignOffset?: number;
  avoidCollisions?: boolean;
  /** What the content keeps within, besides the window: a scrolling panel it belongs to. */
  collisionBoundary?: Element | null;
  collisionPadding?: number;
  /** Hidden while what it's anchored to is out of view within the boundary. */
  hideWhenDetached?: boolean;
  onInteractOutside?: (event: Event) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
}

interface IPopoverCloseProps {
  children: ReactNode;
  asChild?: boolean;
}

const PopoverTrigger: FC<IPopoverTriggerProps> = ({ children, asChild = true, tooltip }) => {
  'use memo';
  const trigger = (
    <RadixPopover.Trigger asChild={asChild} className={styles.trigger}>
      {children}
    </RadixPopover.Trigger>
  );
  return tooltip === undefined ? trigger : <Tooltip title={tooltip}>{trigger}</Tooltip>;
};

const PopoverContent: FC<IPopoverContentProps> = ({
  children,
  side = 'bottom',
  align = 'center',
  sideOffset = 4,
  alignOffset = 0,
  className,
  onInteractOutside,
  onEscapeKeyDown,
  onCloseAutoFocus,
  ...rest
}) => {
  'use memo';
  const windowDoc = useWindowDocument();

  // Focus stays in an iframe the user clicked into, instead of going back to the trigger.
  const handleCloseAutoFocus = (event: Event) => {
    if (windowDoc.activeElement?.tagName === 'IFRAME') event.preventDefault();
    onCloseAutoFocus?.(event);
  };

  return (
    <RadixPopover.Portal container={windowDoc.body}>
      <RadixPopover.Content
        className={classNames(styles.content, className)}
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        onInteractOutside={onInteractOutside}
        onEscapeKeyDown={onEscapeKeyDown}
        onCloseAutoFocus={handleCloseAutoFocus}
        {...rest}
      >
        {children}
      </RadixPopover.Content>
    </RadixPopover.Portal>
  );
};

const PopoverClose: FC<IPopoverCloseProps> = ({ children, asChild = true }) => {
  'use memo';
  return <RadixPopover.Close asChild={asChild}>{children}</RadixPopover.Close>;
};

type IPopoverAnchorProps =
  | { children: ReactNode; asChild?: boolean; element?: never }
  // An element, or anything that can say where it is now (an anchor that is repainted).
  | { children?: never; asChild?: boolean; element: Measurable | null };

const PopoverAnchor: FC<IPopoverAnchorProps> = ({ children, asChild = true, element }) => {
  'use memo';

  const virtualRef = useMemo(() => ({ current: element ?? null }) as RefObject<Measurable>, [element]);

  if (element !== undefined) {
    return <RadixPopover.Anchor virtualRef={virtualRef} />;
  }

  return <RadixPopover.Anchor asChild={asChild}>{children}</RadixPopover.Anchor>;
};

const PopoverRoot: FC<IPopoverProps> = ({
  children,
  open,
  onOpenChange,
  defaultOpen = false,
  modal = false,
  closeOnWindowBlur = false,
}) => {
  'use memo';
  const windowDoc = useWindowDocument();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isOpen = open ?? uncontrolledOpen;

  const handleOpenChange = (next: boolean) => {
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const close = useEffectEvent(() => handleOpenChange(false));

  useEffect(() => {
    const win = windowDoc.defaultView;
    if (!closeOnWindowBlur || !isOpen || !win) return undefined;
    win.addEventListener('blur', close);
    return () => win.removeEventListener('blur', close);
  }, [closeOnWindowBlur, isOpen, windowDoc]);

  return (
    <RadixPopover.Root open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
      {children}
    </RadixPopover.Root>
  );
};

export const Popover = Object.assign(PopoverRoot, {
  Trigger: PopoverTrigger,
  Content: PopoverContent,
  Close: PopoverClose,
  Anchor: PopoverAnchor,
  Arrow: RadixPopover.Arrow,
});
